import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roof-coating-recoating";
const NAME = "Foam Roof Coating & Recoating";
const TITLE = "Foam Roof Coating & Recoating in Phoenix | Durafoam";
const DESCRIPTION =
  "Is your foam roof chalky, cracked or faded? A Phoenix recoat every 5 years protects the foam and saves a costly replacement. Free inspection, family-owned since 1989.";


const faqs = [
  {
    q: "How often does a foam roof need recoating?",
    a: "In Phoenix, every 5 years. The Valley's 11+ UV index burns through elastomeric coatings far faster than manufacturer literature suggests. South- and west-facing slopes wear first — we'll inspect and tell you exactly where you are in the coating's life.",
  },
  {
    q: "What happens if I skip the recoat?",
    a: "Once the coating fails, UV starts breaking down the foam itself. A skipped recoat that turns into foam damage typically costs 3–5× more to fix than the recoat would have. It's the single biggest cause of premature foam roof failure in the Valley.",
  },
  {
    q: "Which coating system do you use?",
    a: "We specify the system based on your roof's condition and exposure. We use proven manufacturers — silicone, acrylic, or urethane elastomerics — and write the exact product name and mil thickness into your estimate. No mystery coatings.",
  },
  {
    q: "How long does a recoat take?",
    a: "Most Phoenix homes recoat in 1–3 days depending on roof size, prep needs, and weather. We pressure-clean and prime day one, then apply two coats over the following days. You can stay in the home the entire time.",
  },
  {
    q: "Do you offer warranties on recoats?",
    a: "Every recoat in Phoenix carries a 5-year Durafoam workmanship warranty. If your roof is still inside a Durafoam install warranty (5-year or 10-year), an on-time recoat renews that warranty for another 5 years — and it can be renewed indefinitely on every subsequent recoat.",
  },
];

export const Route = createFileRoute("/foam-roof-coating-recoating")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="foam-roof-coating-recoating"
      extraSections={SEARCH_TERM_SECTIONS["foam-roof-coating-recoating"]}
      eyebrow="Coating & Recoating"
      h1="Foam Roof Coating & Recoating"
      subhead="A recoat is the cheapest roof you'll ever buy — done on time, it adds another 5 years to your Phoenix foam roof and renews your Durafoam install warranty with it."
      ctaLabel="Request a Recoat Estimate"
      testimonial={{
        quote:
          "I live in a home with a flat roof. Years ago, we had the roof recoated by Durafoam. The results were excellent. Same great insulation, water tight and lower temps inside during the hot summer months. Last year I received a reminder from Durafoam so I again contacted the company. I had the roof recoated again. They did a thorough cleaning and application of new product. Its so nice to work with folks who are thorough, professional and provide consistent results. I highly recommend contacting Durafoam.",
        name: "Claudia Slone",
        source: "Google review",
        context: "Flat roof recoating",
        rating: 5,
      }}
      heroBullets={[
        "Adds another 5 years to the roof",
        "Renews your Durafoam install warranty by 5 years",
        "Restores reflectivity and energy savings",
        "Documented mil thickness on every job",
      ]}
      whatIs={{
        heading: "Why recoats matter",
        body: (
          <>
            <p>
              Spray foam roofing is a two-part system: rigid foam that insulates and shapes the
              roof, and an elastomeric coating that protects the foam from UV and abrasion. The
              coating is sacrificial — it's designed to wear so the foam doesn't. When you recoat
              on schedule, the foam underneath stays pristine and the whole roof simply resets
              its clock.
            </p>
            <p>
              Skipping a recoat is the single most expensive mistake a foam roof owner can make.
              Once UV reaches the foam, it starts breaking it down — chalky surfaces, friable
              edges, and eventually moisture intrusion. At that point you're paying to remove and
              replace damaged foam before you can even coat again.
            </p>
            <p>
              We track our Phoenix customers' coating cycles and remind them when the next recoat
              is due. A timely recoat at the typical Phoenix cost of $1.00–$3.00 per square foot
              protects a foam roof that would cost $5–$9 per square foot to replace. You can{" "}
              <Link
                to="/foam-roof-recoating-cost-in-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                see foam roof recoating cost ranges in Phoenix
              </Link>{" "}
              for a fuller breakdown. Cost the blocker? <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Ask us about payment options</Link>.
            </p>

          </>
        ),
      }}
      climate={{
        heading: "Recoats and the Phoenix sun",
        body: (
          <>
            <p>
              The Valley's UV index sits at 11+ for six months of the year. That kind of exposure
              ages an elastomeric coating dramatically faster than a coastal or northern climate.
              Manufacturer recoat intervals written for milder regions don't apply here — in
              Phoenix, plan on a fresh coat every 5 years regardless of what the can says.
            </p>
            <ul>
              <li>
                <strong>South and west exposures wear first.</strong> We commonly see south-slope
                chalking 2–3 years before the rest of the roof shows it. A partial recoat on the
                aggressive faces can buy you a few years before the full recoat.
              </li>
              <li>
                <strong>Granulated coatings hold longer.</strong> Embedded ceramic granules add
                UV resistance and walkability — well worth the small upgrade on south slopes and
                around HVAC.
              </li>
              <li>
                <strong>Reflectivity drops fastest.</strong> A fresh coating reflects 80–85% of
                solar radiation. By year 5 in Phoenix that's dropped into the 60s. Recoating
                immediately restores AC savings homeowners notice on the next utility bill.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our recoating process",
        steps: [
          {
            title: "Inspection and prep recommendation",
            body: "We measure coating thickness, check for foam damage, and document any repairs needed before the recoat goes on. You see photos of everything.",
          },
          {
            title: "Pressure cleaning",
            body: "The entire surface is pressure-cleaned to remove chalking, dust, and contaminants. Coatings only bond to a clean substrate.",
          },
          {
            title: "Repairs and primer",
            body: "Bird pecks and small foam voids are repaired. A primer or bond coat is applied where the manufacturer specifies it for adhesion.",
          },
          {
            title: "Two-coat application",
            body: "Coating is applied in two crosshatched passes at documented mil thickness. We can show wet-mil readings on request.",
          },
        ],
      }}
      extraSection={{
        heading: "Recoats across the Valley",
        body: (
          <>
            <p>
              Recoating is the routine maintenance that keeps a foam roof going for decades, and we run
              recoat routes across the Valley every week. Most of our recurring work lives in{" "}
              <Link to="/phoenix-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">central Phoenix</Link>,{" "}
              <Link to="/mesa-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">the East Valley in Mesa</Link>{" "}
              and{" "}
              <Link to="/chandler-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Chandler</Link>, plus West Valley recoats in{" "}
              <Link to="/peoria-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Peoria's Arrowhead and Vistancia neighborhoods</Link>{" "}
              and{" "}
              <Link to="/surprise-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Surprise's Sun City-adjacent subdivisions</Link>. We also run recoats in{" "}
              <Link to="/scottsdale-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Scottsdale</Link>,{" "}
              <Link to="/paradise-valley-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Paradise Valley</Link>,{" "}
              <Link to="/carefree-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Carefree</Link>, and{" "}
              <Link to="/cave-creek-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Cave Creek</Link>. We keep recoat reminders on
              file so your foam never drifts past its service interval.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Is your coating due?"
      ctaBody="If your Phoenix foam roof is 4+ years old, it's time for an inspection. We'll tell you exactly where you are in the coating's life — no pressure to recoat early."
    />
  );
}
